/*jshint browserify: true */
/*global PDFedit, angular, PDFJS, document*/
'use strict';

module.exports = drvPdfRender;

var contextMenuTemplates = require('./ContextMenu');
var propertiesDialogTemplates = require('./PropertiesDialog');
var fontList = require('../Script/pdf-font-list.js');

drvPdfRender.$inject = ['$window', '$compile', '$q', '$templateRequest', 'pdfedit_dialog', 'pdfedit_zoomPresets'];

/**
 * @ngdoc directive
 * @name drvPdfRender
 *
 * @description Display a PDF and allow some user interaction with it.
 *
 * # Scope Properties
 *
 * {Uint8Array} document - A Uint8Array representing the PDF
 * {number} scale - Drawing scale aka zoom for the PDF, 1.0 = 100%
 * {Array} pdfElements - Array of all PDF elements on the document.
 * {Array} selectedPdfElements - Array of PDF elements that have been selected by the user.
 * {string} interactionMode
 * {Object} api - An object reference. This object will have API methods added to it.
 * {Object} defaultFont - A default font object with one or more font properties.
 *   {number} defaultFont.fontSize
 *   {string} defaultFont.fontName
 *   {string} defaultFont.fontColour
 *   {bool} defaultFont.fontBold
 *   {bool} defaultFont.fontItalic
 * {Object} contextMenuTemplates - An object for overriding context menu templates. The following
 *   templates may be overridden:
 *   - textElement
 *   - tickBoxElement
 *   - imageElement
 *   - multipleElements
 *   - noSelection
 *   Specify an override using a templateUrl property, e.g.
 *   contextMenuTemplates.noSelection.templateUrl = 'app/blah/no-selection.html'
 * {function} onOpenPropertiesDialog - Called when the user tries to open the properties dialog
 *   for one or more selected PDF elements (textbox, imagebox, etc.). Function should return
 *   true if the default dialog is to be displayed.
 */
function drvPdfRender($window, $compile, $q, $templateRequest, pdfedit_dialog, pdfedit_zoomPresets) {
    var directive = {
        link: link,
        restrict: 'EA',
        scope: {
            document: '=',
            scale: '=?',
            pdfElements: '=?',
            selectedPdfElements: '=?',
            interactionMode: '=?',
            api: '=?',
            readOnlyMode: '<?',
            defaultFont: '<?',
            contextMenuTemplates: '<?',
            onOpenPropertiesDialog: '&?'
        }
    };
    return directive;

    function link(scope, element, attrs) {

        // This would be better off done somewhere else, and not quite so hard-coded
        PDFJS.workerSrc = 'bower_components/pdfjs-dist/build/pdf.worker.js';

        var containerElement = element[0];

        var pageContainerTemplate = '<div style="position: relative; margin: 6px auto 18px auto; box-shadow: 3px 3px 5px 5px #ccc;">' +
            '<canvas style="position: absolute;" id="pdf-canvas"></canvas>' +
            '<canvas style="position: absolute; border: 1px solid black;" id="pdf-overlay-canvas"></canvas>' +
            '</div>';

        var mousePosition = { clientX: 0, clientY: 0 };
        var pages = [];
        var interactionModeObj;
        var contextMenu = new PDFedit.ContextMenu(element[0]);
        var clipboard = [];

        setScopeDefaults(scope);

        scope.api.zoomIn = zoomIn;
        scope.api.zoomOut = zoomOut;
        scope.api.setSelectMode = setSelectMode;
        scope.api.setAddTextMode = setAddTextMode;
        scope.api.setAddTickBoxMode = setAddTickBoxMode;
        scope.api.setAddImageMode = setAddImageMode;
        scope.api.deleteSelectedElements = deleteSelectedElements;
        scope.api.cutSelection = cutSelection;
        scope.api.copySelection = copySelection;
        scope.api.paste = paste;
        scope.api.closeContextMenu = closeContextMenu;
        scope.api.selectAll = selectAll;
        scope.api.alignTopOfElements = alignTopOfElements;
        scope.api.alignBottomOfElements = alignBottomOfElements;
        scope.api.alignLeftOfElements = alignLeftOfElements;
        scope.api.alignRightOfElements = alignRightOfElements;

        Object.defineProperty(scope, 'pasteEnabled', {
            get: function () { return clipboard.length > 0; }
        });
        scope.deleteSelectedElements = deleteSelectedElements;
        scope.cutSelection = cutSelection;
        scope.copySelection = copySelection;
        scope.paste = paste;
        scope.closeContextMenu = closeContextMenu;
        scope.setAddTextMode = setAddTextMode;
        scope.setAddTickBoxMode = setAddTickBoxMode;
        scope.setAddImageMode = setAddImageMode;
        scope.fontList = fontList;
        scope.openPropertiesDialog = openPropertiesDialog;
        scope.selectAll = selectAll;
        scope.alignTopOfElements = alignTopOfElements;
        scope.alignBottomOfElements = alignBottomOfElements;
        scope.alignLeftOfElements = alignLeftOfElements;
        scope.alignRightOfElements = alignRightOfElements;
        scope.makeElementsSameWidth = makeElementsSameWidth;
        scope.makeElementsSameHeight = makeElementsSameHeight;
        scope.makeElementsSameSize = makeElementsSameSize;

        scope.$watchCollection(function () { return PDFedit.elements.PdfElement.colourSettings; }, function (newVal) {
            renderOverlay();
        });

        scope.$watch('document', function (newVal, oldVal) {
            loadPdf().then(renderOverlay);
        });

        scope.$watch('scale', function (newVal, oldVal) {
            if (newVal !== oldVal) {

                var scrollDestination = null;

                if ($window.scrollY !== 0) {
                    // Find the page currently visible
                    var focusedPage = null;
                    var pageClientRect;
                    for (var i = 0; i < pages.length; i++) {
                        pageClientRect = pages[i].containerElement.getBoundingClientRect();
                        if (pageClientRect.top < $window.innerHeight && pageClientRect.bottom > 0) {
                            focusedPage = pages[i];
                            i = pages.length;
                        }
                    }

                    // This number is just a fudge to account for fixed toolbars at the top of the view
                    var pageTopOffset = 190;

                    // Determine a scroll position to be applied after the zoom is changed
                    if (focusedPage !== null) {
                        var pageRelativeTop = (pageClientRect.top - pageTopOffset) / focusedPage.scale;
                        var desiredTop = pageRelativeTop * newVal;
                        scrollDestination = $window.scrollY - (desiredTop - pageClientRect.top + pageTopOffset);
                    }
                }

                angular.forEach(pages, function (page) {
                    page.scale = newVal;
                    page.renderPage();
                });

                renderOverlay();

                // Scroll to the same position in the document as before zoom was changed
                if (scrollDestination !== null) {
                    $window.scrollTo(0, scrollDestination);
                }
            }
        });

        scope.$watch('pdfElements', function (newVal, oldVal) {
            if (newVal !== oldVal) {
                renderOverlay();
            }
        }, true);

        scope.$watchCollection('selectedPdfElements', function (newVal, oldVal) {
            if (newVal !== oldVal) {
                renderOverlay();
            }
        });

        scope.$watch('interactionMode', function (newVal, oldVal) {
            if (!(interactionModeObj instanceof PDFedit.uimodes[newVal])) {
                interactionModeObj = new PDFedit.uimodes[scope.interactionMode](scope.pdfElements, scope.selectedPdfElements, pages);

                if (interactionModeObj instanceof PDFedit.uimodes.AddTextMode) {
                    scope.selectedPdfElements.length = 0;

                    if (angular.isDefined(scope.defaultFont) && scope.defaultFont !== null) {
                        interactionModeObj.defaultFont = scope.defaultFont;
                    }
                }

                interactionModeObj.addPdfElement(function (newElement) {
                    scope.pdfElements.push(newElement);
                    scope.selectedPdfElements.length = 1;
                    scope.selectedPdfElements[0] = newElement;
                    scope.interactionMode = 'SelectionMode';
                });
                interactionModeObj.pdfElements = scope.pdfElements;
                interactionModeObj.selectedPdfElements = scope.selectedPdfElements;

                containerElement.style.cursor = interactionModeObj.cursor;
                interactionModeObj.cursorChanged(function (cursor) {
                    containerElement.style.cursor = cursor;
                });
            }
        });


        $window.addEventListener('mouseup', function (event) {
            if (!scope.readOnlyMode) {
                scope.$apply(function () {
                    angular.forEach(pages, function (page) {
                        interactionModeObj.onMouseUp(event, page);
                    });

                    renderOverlay();
                });
            }
        });
            
        $window.addEventListener('mousemove', function (event) {
            mousePosition.clientX = event.clientX;
            mousePosition.clientY = event.clientY;
        });

        function eventHandlerMouseDown(mouseEvent, page) {
            if (!scope.readOnlyMode) {
                interactionModeObj.onMouseDown(mouseEvent, page);
                renderOverlay();
            }
        }

        function eventHandlerMouseClick(mouseEvent, page) {
            if (!scope.readOnlyMode) {
                scope.$apply(function () {
                    interactionModeObj.onMouseClick(mouseEvent, page);
                    renderOverlay();
                });
            }
        }

        function eventHandlerMouseDoubleClick(mouseEvent, page) {
            if (!scope.readOnlyMode) {
                mouseEvent.preventDefault();

                // In Google Chrome, preventDefault isn't enough to prevent dblclick from making a selection,
                // so, explicitly clear the selection here to be sure.
                if ($window.getSelection) {
                    $window.getSelection().removeAllRanges();
                }

                openPropertiesDialog();
            }
        }

        function eventHandlerContextMenu(mouseEvent, page) {
            if (!scope.readOnlyMode && interactionModeObj instanceof PDFedit.uimodes.SelectionMode) {

                //
                // Change selection, if click location isn't on an element that's already part of the selection
                //

                var position = PDFedit.coordinates.Position.fromMouseEvent(mouseEvent, page.pageViewport, page.pdfOverlayCanvasElement);
                var hitElement = null;
                for (var i = 0; i < scope.pdfElements.length; i++) {
                    if (scope.pdfElements[i].pageNumber === page.pageNumber && scope.pdfElements[i].hitTest(page.pageViewport, page.pdfOverlayCanvasElement, position)) {
                        hitElement = scope.pdfElements[i];
                        i = scope.pdfElements.length;
                    }
                }

                if (hitElement === null) {
                    scope.$apply(function () {
                        scope.selectedPdfElements.length = 0;
                    });
                } else if (scope.selectedPdfElements.indexOf(hitElement) === -1) {
                    scope.$apply(function () {
                        scope.selectedPdfElements.length = 0;
                        scope.selectedPdfElements.push(hitElement);
                    });
                }


                //
                // Show context-menu based on selection
                //

                var contextMenuTemplatePropertyName;
                if (scope.selectedPdfElements.length === 1 && scope.selectedPdfElements[0] instanceof PDFedit.elements.PdfTextElement) {
                    contextMenuTemplatePropertyName = 'textElement';
                } else if (scope.selectedPdfElements.length === 1 && scope.selectedPdfElements[0] instanceof PDFedit.elements.PdfTickBoxElement) {
                    contextMenuTemplatePropertyName = 'tickBoxElement';
                } else if (scope.selectedPdfElements.length === 1 && scope.selectedPdfElements[0] instanceof PDFedit.elements.PdfImageElement) {
                    contextMenuTemplatePropertyName = 'imageElement';
                } else if (scope.selectedPdfElements.length > 1) {
                    contextMenuTemplatePropertyName = 'multipleElements';
                } else {
                    contextMenuTemplatePropertyName = 'noSelection';
                }

                var templateHtmlPromise = null;

                if (angular.isDefined(scope.contextMenuTemplates) &&
                    scope.contextMenuTemplates !== null &&
                    angular.isDefined(scope.contextMenuTemplates[contextMenuTemplatePropertyName]) && 
                    scope.contextMenuTemplates[contextMenuTemplatePropertyName] !== null) {

                    if (angular.isDefined(scope.contextMenuTemplates[contextMenuTemplatePropertyName].templateUrl) &&
                        scope.contextMenuTemplates[contextMenuTemplatePropertyName].templateUrl !== null) {

                        templateHtmlPromise = $templateRequest(scope.contextMenuTemplates[contextMenuTemplatePropertyName].templateUrl);
                    }
                }

                if (templateHtmlPromise === null) {
                    templateHtmlPromise = $q.when(contextMenuTemplates[contextMenuTemplatePropertyName]);
                }

                templateHtmlPromise.then(function (templateHtml) {
                    if (templateHtml !== null) {
                        contextMenu.page = page;
                        contextMenu.pagePosition = PDFedit.coordinates.Position.fromMouseEvent(mouseEvent, page.pageViewport, page.pdfOverlayCanvasElement);
                        var menuElement = contextMenu.show(mouseEvent.clientX, mouseEvent.clientY, templateHtml);
                        $compile(menuElement)(scope);
                        //scope.$digest();
                    } else {
                        contextMenu.close();
                    }
                });
            }

            mouseEvent.preventDefault();
        }

        function eventHandlerMouseMove(mouseEvent, page) {
            if (!scope.readOnlyMode) {
                var position = PDFedit.coordinates.Position.fromMouseEvent(mouseEvent, page.pageViewport, page.pdfOverlayCanvasElement);

                interactionModeObj.onMouseMove(mouseEvent, page);
                renderOverlay();

                // To prevent other event handlers changing the pointer to a text I-beam
                mouseEvent.preventDefault();
            }
        }

        function deleteSelectedElements() {
            if (!scope.readOnlyMode) {
                var indexOfSelected;
                for (var i = scope.selectedPdfElements.length - 1; i >= 0; i--) {
                    indexOfSelected = scope.pdfElements.indexOf(scope.selectedPdfElements[i]);
                    if (indexOfSelected !== -1) {
                        scope.pdfElements.splice(indexOfSelected, 1);
                    }
                }
                scope.selectedPdfElements.length = 0;
            }
        }

        function openPropertiesDialog() {
            if (scope.selectedPdfElements.length > 0) {
                var presentDialog = true;

                if (angular.isFunction(scope.onOpenPropertiesDialog)) {
                    var stillUseDefault = scope.onOpenPropertiesDialog();
                    if (angular.isUndefined(stillUseDefault) || stillUseDefault === null || !stillUseDefault) {
                        presentDialog = false;
                    }
                }

                if (presentDialog) {
                    if (scope.selectedPdfElements[0] instanceof PDFedit.elements.PdfTextElement) {
                        pdfedit_dialog.showDialog(propertiesDialogTemplates.textElement, scope);
                    } else if (scope.selectedPdfElements[0] instanceof PDFedit.elements.PdfTickBoxElement) {
                        pdfedit_dialog.showDialog(propertiesDialogTemplates.tickBoxElement, scope);
                    } else if (scope.selectedPdfElements[0] instanceof PDFedit.elements.PdfImageElement) {
                        pdfedit_dialog.showDialog(propertiesDialogTemplates.imageElement, scope);
                    }
                }
            }
        }

        function cutSelection() {
            copySelection();
            deleteSelectedElements();
        }

        function copySelection () {
            clipboard = angular.copy(scope.selectedPdfElements);
        }

        function paste () {
            if (contextMenu.page !== undefined && contextMenu.page !== null &&
                contextMenu.pagePosition !== undefined && contextMenu.pagePosition !== null) {

                var pasteData = angular.copy(clipboard);
                if (pasteData.length) {
                    var i;

                    var boundingBoxLeft = pasteData[0].x;
                    var boundingBoxTop = pasteData[0].y + pasteData[0].height;
                    for (i = 1; i < pasteData.length; i++) {
                        if (pasteData[i].x < boundingBoxLeft) { boundingBoxLeft = pasteData[i].x; }
                        if (pasteData[i].y + pasteData[0].height > boundingBoxTop) { boundingBoxTop = pasteData[i].y + pasteData[0].height; }
                    }

                    for (i = 0; i < pasteData.length; i++) {
                        // Paste all elements to the same page
                        pasteData[i].pageNumber = contextMenu.page.pageNumber;
                    }

                    // Reposition all elements so the click position represents the top-left of the bounding box of all elements
                    for (i = 0; i < pasteData.length; i++) {
                        pasteData[i].x = pasteData[i].x - boundingBoxLeft + contextMenu.pagePosition.pdfX;
                        pasteData[i].y = pasteData[i].y + contextMenu.pagePosition.pdfY - boundingBoxTop;
                    }

                    for (i = 0; i < pasteData.length; i++) {
                        scope.pdfElements.push(pasteData[i]);
                    }
                }
            }
        }

        function closeContextMenu () {
            contextMenu.close();
        }

        function zoomIn() {
            var zoomIndex = -1;

            for (var i = 0; i < pdfedit_zoomPresets.length; i++) {
                if (scope.scale >= pdfedit_zoomPresets[i]) {
                    zoomIndex = i;
                }
            }

            if (zoomIndex !== -1 && zoomIndex < pdfedit_zoomPresets.length - 1) {
                scope.scale = pdfedit_zoomPresets[zoomIndex + 1];
            }
        }

        function zoomOut() {
            var zoomIndex = -1;

            for (var i = pdfedit_zoomPresets.length - 1; i >= 0; i--) {
                if (scope.scale <= pdfedit_zoomPresets[i]) {
                    zoomIndex = i;
                }
            }

            if (zoomIndex > 0) {
                scope.scale = pdfedit_zoomPresets[zoomIndex - 1];
            }
        }

        function setSelectMode() {
            scope.interactionMode = 'SelectionMode';
        }

        function setAddTextMode() {
            scope.interactionMode = 'AddTextMode';
        }

        function setAddTickBoxMode() {
            scope.interactionMode = 'AddTickBoxMode';
        }

        function setAddImageMode() {
            scope.interactionMode = 'AddImageMode';
        }

        function selectAll() {
            if (!scope.readOnlyMode) {
                scope.selectedPdfElements.length = 0;

                for (var i = 0; i < scope.pdfElements.length; i++) {
                    scope.selectedPdfElements.push(scope.pdfElements[i]);
                }
            }
        }

        function alignTopOfElements() {
            if (!scope.readOnlyMode && scope.selectedPdfElements.length > 1) {
                var yPosTop = scope.selectedPdfElements[0].y + scope.selectedPdfElements[0].height;
                var pageNumber = scope.selectedPdfElements[0].pageNumber;
                for (var i = 1; i < scope.selectedPdfElements.length; i++) {
                    scope.selectedPdfElements[i].y = yPosTop - scope.selectedPdfElements[i].height;
                    scope.selectedPdfElements[i].pageNumber = pageNumber;
                }
            }
        }

        function alignBottomOfElements() {
            if (!scope.readOnlyMode && scope.selectedPdfElements.length > 1) {
                var yPos = scope.selectedPdfElements[0].y;
                var pageNumber = scope.selectedPdfElements[0].pageNumber;
                for(var i = 1; i < scope.selectedPdfElements.length; i++) {
                    scope.selectedPdfElements[i].y = yPos;
                    scope.selectedPdfElements[i].pageNumber = pageNumber;
                }
            }
        }

        function alignLeftOfElements() {
            if (!scope.readOnlyMode && scope.selectedPdfElements.length > 1) {
                var xPos = scope.selectedPdfElements[0].x;
                for (var i = 1; i < scope.selectedPdfElements.length; i++) {
                    scope.selectedPdfElements[i].x = xPos;
                }
            }
        }

        function alignRightOfElements() {
            if (!scope.readOnlyMode && scope.selectedPdfElements.length > 1) {
                var xPosRight = scope.selectedPdfElements[0].x + scope.selectedPdfElements[0].width;
                for (var i = 1; i < scope.selectedPdfElements.length; i++) {
                    scope.selectedPdfElements[i].x = xPosRight -scope.selectedPdfElements[i].width;
                }
            }
        }

        function makeElementsSameWidth() {
            if (!scope.readOnlyMode && scope.selectedPdfElements.length > 1) {
                var width = scope.selectedPdfElements[0].width;
                for (var i = 1; i < scope.selectedPdfElements.length; i++) {
                    scope.selectedPdfElements[i].width = width;
                }
            }
        }

        function makeElementsSameHeight() {
            if (!scope.readOnlyMode && scope.selectedPdfElements.length > 1) {
                var height = scope.selectedPdfElements[0].height;
                for (var i = 1; i < scope.selectedPdfElements.length; i++) {
                    scope.selectedPdfElements[i].height = height;
                }
            }
        }

        function makeElementsSameSize() {
            if (!scope.readOnlyMode && scope.selectedPdfElements.length > 1) {
                var width = scope.selectedPdfElements[0].width;
                var height = scope.selectedPdfElements[0].height;
                for (var i = 1; i < scope.selectedPdfElements.length; i++) {
                    scope.selectedPdfElements[i].width = width;
                    scope.selectedPdfElements[i].height = height;
                }
            }
        }



        /**
         * Render the user interface overlay.
         * Overlay is how the user "draws" things onto the PDF, and also highlights user selection, drag-drop, etc.
         */
        function renderOverlay() {
            angular.forEach(pages, function (page) {
                page.pdfOverlayCanvasContext.clearRect(0, 0, page.pdfOverlayCanvasElement.width, page.pdfOverlayCanvasElement.height);

                // When in selection mode, style as 'selected', only those elements within the selection box being drawn
                var candidateSelectedElements =[];
                var inSelectionMode = (interactionModeObj instanceof PDFedit.uimodes.SelectionMode) &&
                    interactionModeObj._subMode === PDFedit.uimodes.SelectionMode.subModes.SELECT;
                if (inSelectionMode) {
                    candidateSelectedElements = interactionModeObj.getElementsWithinSelectionRectangle();
                }

                var mousePositionOnPage = PDFedit.coordinates.Position.fromMouseEvent(mousePosition, page.pageViewport, page.pdfOverlayCanvasElement);

                angular.forEach(scope.pdfElements, function (pdfElement) {
                    if (pdfElement.pageNumber === page.pageNumber) {
                        var selected = (inSelectionMode && candidateSelectedElements.indexOf(pdfElement) !== -1) ||
                            (!inSelectionMode && scope.selectedPdfElements.indexOf(pdfElement) !== -1);
                        pdfElement.renderToCanvas(page.pageViewport, page.pdfOverlayCanvasContext, selected, mousePositionOnPage, interactionModeObj);
                    }
                });

                interactionModeObj.renderToCanvas(page);
            });
        }

        /**
         * Load PDF from document in scope.
         * Method is asynchronous.
         */
        function loadPdf() {
            element[0].innerHTML = '';
            pages.length = 0;

            if (angular.isDefined(scope.document) && scope.document !== null) {
                return PDFJS.getDocument(scope.document).then(function getDocumentOk(pdf) {
                    var pageNumber = 1;

                    return (function loadPage() {
                        return pdf.getPage(pageNumber).then(function getPageOk(pdfPage) {
                            var pageContainer = document.createElement('div');
                            pageContainer.innerHTML = pageContainerTemplate;
                            pageContainer.className = 'page-container';
                            pageContainer.setAttribute('data-page-number', pageNumber);
                            element[0].appendChild(pageContainer);

                            var pdfCanvasElement = pageContainer.querySelector('#pdf-canvas');
                            var pdfOverlayCanvasElement = pageContainer.querySelector('#pdf-overlay-canvas');
                            var containerElement = pageContainer.querySelector('div');

                            var page = new PDFedit.document.Page(pdfPage, pageNumber, containerElement, pdfCanvasElement, pdfOverlayCanvasElement, scope.scale);

                            page.pdfOverlayCanvasElement.addEventListener('mousemove', function (event) {
                                eventHandlerMouseMove(event, page);
                            });
                            page.pdfOverlayCanvasElement.addEventListener('contextmenu', function (event) {
                                eventHandlerContextMenu(event, page);
                            });
                            page.pdfOverlayCanvasElement.addEventListener('click', function (event) {
                                eventHandlerMouseClick(event, page);
                            });
                            page.pdfOverlayCanvasElement.addEventListener('dblclick', function (event) {
                                eventHandlerMouseDoubleClick(event, page);
                            });
                            page.pdfOverlayCanvasElement.addEventListener('mousedown', function (event) {
                                eventHandlerMouseDown(event, page);
                            });

                            page.renderPage();

                            pages.push(page);

                            if (pageNumber < pdf.numPages) {
                                pageNumber++;
                                return loadPage();
                            }
                        });
                    })();
                });
            } else {
                return $q.resolve();
            }
        }
    }

    /**
     * Some properties on the scope are optional. Set defaults if they weren't specified.
     */
    function setScopeDefaults(scope) {
        if (angular.isUndefined(scope.pdfElements) || scope.pdfElements === null) {
            scope.pdfElements = [];
        }

        if (angular.isUndefined(scope.selectedPdfElements) || scope.selectedPdfElements === null) {
            scope.selectedPdfElements = [];
        }

        if (angular.isUndefined(scope.scale) || scope.scale === null) {
            scope.scale = 1.0;
        }

        if (angular.isUndefined(scope.interactionMode) || scope.interactionMode === null || scope.interactionMode === '') {
            scope.interactionMode = 'SelectionMode';
        }

        if (angular.isUndefined(scope.readOnlyMode) || scope.readOnlyMode === null) {
            scope.readOnlyMode = false;
        }

        scope.api = scope.api || {};
    }
}